/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  DEMOSITEBANNER.TSX - Demo Site Banner (demo.nt3d.fun)                    ║
 * ║  Shows when site is running in demo mode with testnet connection          ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ethers } from 'ethers';
import toast from 'react-hot-toast';

const IS_DEMO_SITE = import.meta.env.VITE_DEMO_MODE === 'true';
const PAW_TOKEN_ADDRESS = import.meta.env.VITE_PAW_TOKEN_ADDRESS;

// Simple ERC20 ABI for balance check
const ERC20_ABI = [
  'function balanceOf(address) view returns (uint256)',
  'function decimals() view returns (uint8)',
];

interface DemoSiteBannerProps {
  walletAddress?: string;
}

export function DemoSiteBanner({ walletAddress }: DemoSiteBannerProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isRequestingFaucet, setIsRequestingFaucet] = useState(false);
  const [pawBalance, setPawBalance] = useState<string | null>(null);

  // Request test PAW from faucet
  const requestFaucet = useCallback(async () => {
    if (!walletAddress) {
      toast.error('Connect your wallet first!');
      return;
    }

    setIsRequestingFaucet(true);
    
    try {
      // Call faucet API - normalize address to lowercase
      const response = await fetch('https://nt3d-faucet.wurkit.workers.dev/request', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ address: walletAddress.toLowerCase() }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Faucet HTTP error:', response.status, errorText);
        toast.error(`Faucet error: ${response.status}`);
        return;
      }

      const data = await response.json();

      if (data.success) {
        toast.success(
          <div>
            <strong>🚰 Faucet Success!</strong>
            <br />
            <span className="text-sm">Sent {data.amount} test PAW to your wallet</span>
          </div>,
          { duration: 5000 }
        );
        // Refresh balance after successful faucet
        setTimeout(() => checkBalance(), 3000);
      } else {
        toast.error(data.error || 'Faucet request failed');
      }
    } catch (error) {
      console.error('Faucet error:', error);
      toast.error(`Faucet error: ${error instanceof Error ? error.message : 'Network error'}`);
    } finally {
      setIsRequestingFaucet(false);
    }
  }, [walletAddress]);

  // Format large numbers with proper units (K, M, B, T)
  const formatBalance = (num: number): string => {
    if (num >= 1e12) {
      return `${(num / 1e12).toFixed(2)}T`;
    } else if (num >= 1e9) {
      return `${(num / 1e9).toFixed(1)}B`;
    } else if (num >= 1e6) {
      return `${(num / 1e6).toFixed(1)}M`;
    } else if (num >= 1e3) {
      return `${(num / 1e3).toFixed(1)}K`;
    } else {
      return num.toFixed(0);
    }
  };

  // Check PAW balance
  const checkBalance = useCallback(async () => {
    if (!walletAddress || !window.ethereum) return;

    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const token = new ethers.Contract(PAW_TOKEN_ADDRESS, ERC20_ABI, provider);
      const balance = await token.balanceOf(walletAddress);
      const decimals = await token.decimals();
      const formatted = ethers.formatUnits(balance, decimals);
      
      // Format nicely with T/B/M/K
      const num = parseFloat(formatted);
      setPawBalance(formatBalance(num));
    } catch (error) {
      console.error('Balance check error:', error);
    }
  }, [walletAddress]);

  // Check balance when wallet connects
  if (walletAddress && !pawBalance) {
    checkBalance();
  }

  if (!IS_DEMO_SITE) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-[100]"
      >
        {/* Main Banner */}
        <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              {/* Left: Demo Badge */}
              <div className="flex items-center gap-3">
                <motion.span
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                  className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-bold uppercase tracking-wider"
                >
                  🎮 DEMO
                </motion.span>
                <span className="text-sm font-medium hidden sm:inline">
                  Base Sepolia Testnet • Fake $PAW • Full Mechanics
                </span>
              </div>

              {/* Right: Actions */}
              <div className="flex items-center gap-3">
                {/* Balance display */}
                {walletAddress && pawBalance && (
                  <div className="bg-white/10 px-3 py-1 rounded-lg text-sm">
                    💰 {pawBalance} PAW
                  </div>
                )}

                {/* Faucet button */}
                <button
                  onClick={requestFaucet}
                  disabled={isRequestingFaucet || !walletAddress}
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm px-4 py-1.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isRequestingFaucet ? (
                    <>
                      <motion.span
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                      >
                        ⏳
                      </motion.span>
                      Getting PAW...
                    </>
                  ) : (
                    <>🚰 Get Test PAW</>
                  )}
                </button>

                {/* Play for real CTA */}
                <a
                  href="https://nt3d.fun"
                  className="bg-white text-purple-600 hover:bg-purple-100 px-4 py-1.5 rounded-lg text-sm font-bold transition-all hidden sm:block"
                >
                  Play for Real →
                </a>

                {/* Collapse toggle */}
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-white/70 hover:text-white p-1"
                >
                  {isExpanded ? '▲' : '▼'}
                </button>
              </div>
            </div>

            {/* Expanded Info */}
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="mt-2 pt-2 border-t border-white/20 text-sm text-white/90 flex flex-wrap gap-4 items-center">
                    <div className="flex items-center gap-2">
                      <span>🔗</span>
                      <span>Network: <strong>Base Sepolia</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>💰</span>
                      <span>Currency: <strong>Test PAW</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>⚡</span>
                      <span>100% mechanics active</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>🎓</span>
                      <span>Learn before you play!</span>
                    </div>
                    <div className="ml-auto flex gap-2">
                      <a
                        href="https://www.alchemy.com/faucets/base-sepolia"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:no-underline text-white/80"
                      >
                        Get Base Sepolia ETH
                      </a>
                      <span className="text-white/40">|</span>
                      <a
                        href="https://nt3d.fun"
                        className="underline hover:no-underline"
                      >
                        Play for real →
                      </a>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>

      {/* Spacer */}
      <div className={isExpanded ? 'h-20' : 'h-12'} />
    </AnimatePresence>
  );
}

export default DemoSiteBanner;
